import React, { useState, useEffect } from 'react';
import { Box, Button, Container, TextField, Typography, Stack, CircularProgress, Alert, Card, CardContent, Grid } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AppAppBar from '../../components/layout/AppAppBar';
import { toast } from 'react-toastify';
import { BASE_URL } from '../../config';
import { ApiManager } from '../../utils/apiManager';

export default function SubmissionRejectedPageAds() {
    const [phone, setPhone] = useState('');
    const [upi, setUpi] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [formName, setFormName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [redirectUrl, setRedirectUrl] = useState('/ads');

    useEffect(() => {
        // Fetch specific redirection URL for rejection page
        const fetchRejectUrl = async () => {
            try {
                const response = await ApiManager.fetch(`https://backend.app20.in/api/form/v2/check-form/?url=reject`);
                if (response.status === 200) {
                    const data = await response.json();
                    if (data.link) {
                        setRedirectUrl(data.link);
                    }
                }
            } catch (err) {
                console.error("Error fetching reject URL:", err);
            }
        };

        fetchRejectUrl();

        // ✅ AUTO-SCROLL LOGIC: Check if user returned from install
        const checkInstallReturn = () => {
            const hasInitiatedInstall = localStorage.getItem('install_initiated_reject');
            if (hasInitiatedInstall === 'true' && document.visibilityState === 'visible') {
                localStorage.removeItem('install_initiated_reject');
                // Small delay to ensure rendering is stable
                setTimeout(() => {
                    const formElement = document.getElementById('upload-form-section');
                    if (formElement) {
                        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                }, 500);
            }
        };

        // Check on mount and visibility change
        checkInstallReturn();
        document.addEventListener("visibilitychange", checkInstallReturn);

        // Guard: Check if user is coming from the Ads flow
        // TEMPORARY: Auto-set for testing
        sessionStorage.setItem('ads_user', 'true');
        sessionStorage.setItem('ad_source', 'ads');

        const isAdsUser = sessionStorage.getItem('ads_user') === 'true' ||
            sessionStorage.getItem('ad_source') === 'ads';

        if (!isAdsUser) {
            console.warn('Unauthorized access to Ads rejection page. Redirecting...');
            window.location.href = '/';
            return;
        }

        // Fetch reject form details from API
        const fetchRejectFormName = async () => {
            try {
                const response = await ApiManager.fetchWithAbort(
                    `https://backend.app20.in/api/form/v2/check-form/?url=reject`
                );

                if (response.status === 200) {
                    const data = await response.json();
                    if (data.name) {
                        setFormName(data.name);
                        localStorage.setItem('current_form_name', data.name);
                        console.log('Reject form name set to:', data.name);
                    } else {
                        setFormName('Kissht Form Reject');
                    }
                } else {
                    setFormName('Kissht Form Reject');
                }
            } catch (err) {
                console.error('Error fetching reject form name:', err);
                setFormName('Kissht Form Reject');
            }
        };

        fetchRejectFormName();

        // Autofill phone number
        const storedPhone = localStorage.getItem('phone');
        if (storedPhone) {
            setPhone(storedPhone);
        } else {
            const userDataStr = localStorage.getItem('user_data');
            if (userDataStr) {
                try {
                    const userData = JSON.parse(userDataStr);
                    if (userData.phone_number) setPhone(userData.phone_number);
                    else if (userData.phone) setPhone(userData.phone);
                } catch (e) {
                    console.error("Error parsing user data", e);
                }
            }
        }

        return () => {
            document.removeEventListener("visibilitychange", checkInstallReturn);
        };
    }, []);

    const isLoggedIn = !!(localStorage.getItem('is_logged_in') || localStorage.getItem('phone'));

    const handleSubmit = async () => {
        setErrorMessage('');

        if (!phone || !upi || !file) {
            setErrorMessage('Please fill all fields and upload a screenshot');
            return;
        }

        if (phone.length !== 10) {
            setErrorMessage('Please enter a valid 10-digit phone number');
            return;
        }

        if (!['6', '7', '8', '9'].includes(phone[0])) {
            setErrorMessage('Phone number must start with 6, 7, 8, or 9');
            return;
        }

        if (!formName) {
            setErrorMessage('Form name not found. Please go back and try again.');
            return;
        }

        setIsSubmitting(true);

        try {
            const formData = new FormData();
            formData.append('name', formName);
            formData.append('phone', phone);
            formData.append('image', file);
            formData.append('upi', upi);
            formData.append('fest', 'False');

            console.log('Resubmitting Form Data (Ads):', { formName, phone, upi, fileName: file.name, source: 'ads' });

            const response = await ApiManager.fetch(`${BASE_URL}/api/form/input-data/`, {
                method: 'POST',
                body: formData,
            });

            const responseText = await response.text();

            if (!response.ok) {
                let errorMsg = 'Submission failed';

                if (response.status === 404) {
                    errorMsg = "Submission not accepted. You may have already completed this offer or it is no longer active.";
                } else if (response.status >= 500) {
                    errorMsg = "System is currently busy processing other requests. Please try again in 30 seconds.";
                } else {
                    try {
                        const errorData = JSON.parse(responseText);
                        if (errorData.message) errorMsg = errorData.message;
                        else if (errorData.error) errorMsg = errorData.error;
                        else if (errorData.detail) errorMsg = errorData.detail;
                        else errorMsg = "Verification failed. Please check your inputs.";
                    } catch (e) {
                        errorMsg = "Unable to verify submission. Please try again.";
                    }
                }

                throw new Error(errorMsg);
            }

            try {
                const data = JSON.parse(responseText || '{}');
                console.log('Resubmission Success (Ads):', data);
                if (data && data.id) {
                    sessionStorage.setItem('submission_id', data.id);
                }
            } catch (e) {
                console.warn('Could not parse success response JSON:', e);
            }

            toast.success('Form submitted successfully! Redirecting to payment processing...');
            setTimeout(() => {
                window.location.href = '/payment-processing-reject';
            }, 2000);
        } catch (e) {
            const errorMsg = (e as Error).message || 'An error occurred during submission';
            setErrorMessage(errorMsg);
            toast.error(errorMsg);
            setIsSubmitting(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
            <AppAppBar mode="light" toggleColorMode={() => { }} simpleNavbar={true} transparentMode={true} showProfile={isLoggedIn} showLogin={false} />

            <Container maxWidth="sm" sx={{ pb: 6, pt: 12 }}>
                {/* Rejection Alert - Red Card */}
                <Card sx={{
                    mb: 5,
                    mx: 1,
                    backgroundColor: '#FF1E00',
                    color: 'white',
                    textAlign: 'center',
                    borderRadius: '12px',
                    boxShadow: '0 8px 20px rgba(255, 51, 51, 0.3)'
                }}>
                    <CardContent sx={{ py: 3 }}>
                        <Typography sx={{ fontWeight: 500, mb: 1, fontFamily: 'SF Pro Display', fontSize: '24px', lineHeight: '41.67px', letterSpacing: '0%', textAlign: 'center' }}>
                            Your payment was rejected
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, opacity: 0.95, fontFamily: 'SF Pro Display' }}>
                            check the screenshot upload instructions below and upload again
                        </Typography>
                    </CardContent>
                </Card>

                {/* Open Task Again Button - Below Red Card */}
                <Box sx={{ mb: 5, display: 'flex', justifyContent: 'center', mx: 1 }}>
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={() => {
                            localStorage.setItem('install_initiated_reject', 'true');
                            window.location.href = redirectUrl;
                        }}
                        sx={{
                            bgcolor: '#FFC107',
                            color: '#000',
                            fontWeight: 700,
                            py: 2,
                            borderRadius: '12px',
                            fontSize: '1.1rem',
                            textTransform: 'none',
                            boxShadow: '0 4px 12px rgba(255, 193, 7, 0.3)',
                            fontFamily: 'SF Pro Display',
                            '&:hover': {
                                bgcolor: '#FFB300',
                                boxShadow: '0 6px 15px rgba(255, 193, 7, 0.4)',
                            }
                        }}
                    >
                        install this app
                    </Button>
                </Box>

                {/* Screenshot Upload Instructions Section */}
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: '#000', fontSize: '32px', fontFamily: 'SF Pro Display' }}>
                        Screenshot Upload Instructions
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#333', fontSize: '16px', fontWeight: 500, fontFamily: 'SF Pro Display' }}>
                        Yeh Screenshots Galat Hain Or Reject Kar Diye Jayenge
                    </Typography>
                </Box>

                {/* Example Images Section */}
                <Box sx={{ mb: 4 }}>
                    <Grid container spacing={1.5}>
                        <Grid item xs={4}>
                            <Box sx={{ aspectRatio: '9/16' }}>
                                <img
                                    src="/reject_img1.png"
                                    alt="Screenshot Format Example 1"
                                    style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={4}>
                            <Box sx={{ aspectRatio: '9/16' }}>
                                <img
                                    src="/reject_img2.png"
                                    alt="Screenshot Format Example 2"
                                    style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={4}>
                            <Box sx={{ aspectRatio: '9/16' }}>
                                <img
                                    src="/reject_img3.png"
                                    alt="Screenshot Format Example 3"
                                    style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={4}>
                            <Box sx={{ aspectRatio: '9/16' }}>
                                <img
                                    src="/reject_img4.png"
                                    alt="Screenshot Format Example 4"
                                    style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={4}>
                            <Box sx={{ aspectRatio: '9/16' }}>
                                <img
                                    src="/reject_img5.png"
                                    alt="Screenshot Format Example 5"
                                    style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={4}>
                            <Box sx={{ aspectRatio: '9/16' }}>
                                <img
                                    src="/reject_img6.png"
                                    alt="Screenshot Format Example 6"
                                    style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* Instructions List */}
                <Stack spacing={2} sx={{ mb: 4, pl: 2 }}>
                    <Typography sx={{ color: '#333', fontWeight: 600, fontSize: '17px', lineHeight: '1.6', letterSpacing: '0%', fontFamily: 'SF Pro Display' }}>
                        1.&nbsp;&nbsp;&nbsp;Only 5 stars rating and no comment.
                    </Typography>
                    <Typography sx={{ color: '#333', fontWeight: 600, fontSize: '17px', lineHeight: '1.6', letterSpacing: '0%', fontFamily: 'SF Pro Display' }}>
                        2.&nbsp;&nbsp;&nbsp;Rating & comment kar diye per rating 3 ya 4 nhi honi chahiiye.
                    </Typography>
                    <Typography sx={{ color: '#333', fontWeight: 600, fontSize: '17px', lineHeight: '1.6', letterSpacing: '0%', fontFamily: 'SF Pro Display' }}>
                        3.&nbsp;&nbsp;&nbsp;Rating & comment kar diye per post karne se pehle ka screenshot upload kiya.
                    </Typography>
                    <Typography sx={{ color: '#333', fontWeight: 600, fontSize: '17px', lineHeight: '1.6', letterSpacing: '0%', fontFamily: 'SF Pro Display' }}>
                        4.&nbsp;&nbsp;&nbsp;No rating and no comment.
                    </Typography>
                    <Typography sx={{ color: '#333', fontWeight: 600, fontSize: '17px', lineHeight: '1.6', letterSpacing: '0%', fontFamily: 'SF Pro Display' }}>
                        5.&nbsp;&nbsp;&nbsp;Full page screenshot along with the respective app, if only review section then rejected.
                    </Typography>
                    <Typography sx={{ color: '#333', fontWeight: 600, fontSize: '17px', lineHeight: '1.6', letterSpacing: '0%', fontFamily: 'SF Pro Display' }}>
                        6.&nbsp;&nbsp;&nbsp;Comment galat hai — sirf emojis nahi, thoda text likho.
                    </Typography>
                    <Typography sx={{ color: '#333', fontWeight: 600, fontSize: '17px', lineHeight: '1.6', letterSpacing: '0%', fontFamily: 'SF Pro Display' }}>
                        7.&nbsp;&nbsp;&nbsp;Review is missing — install screen se kaam nahi chalega.
                    </Typography>
                    <Typography sx={{ color: '#333', fontWeight: 600, fontSize: '17px', lineHeight: '1.6', letterSpacing: '0%', fontFamily: 'SF Pro Display' }}>
                        8.&nbsp;&nbsp;&nbsp;Old review hai — fresh screenshot chahiye same day and date ka.
                    </Typography>
                    <Typography sx={{ color: '#333', fontWeight: 600, fontSize: '17px', lineHeight: '1.6', letterSpacing: '0%', fontFamily: 'SF Pro Display' }}>
                        9.&nbsp;&nbsp;&nbsp;Only rating and no review
                    </Typography>
                    <Typography sx={{ color: '#333', fontWeight: 600, fontSize: '17px', lineHeight: '1.6', letterSpacing: '0%', fontFamily: 'SF Pro Display' }}>
                        10.&nbsp;&nbsp;&nbsp;Rating confirm screen nahi chalega — written comment dikhna chahiye.
                    </Typography>
                    <Typography sx={{ color: '#333', fontWeight: 600, fontSize: '17px', lineHeight: '1.6', letterSpacing: '0%', fontFamily: 'SF Pro Display' }}>
                        11.&nbsp;&nbsp;&nbsp;Typing screen nahi chahiye — posted review ka screenshot chahiye.
                    </Typography>
                    <Typography sx={{ color: '#333', fontWeight: 600, fontSize: '17px', lineHeight: '1.6', letterSpacing: '0%', fontFamily: 'SF Pro Display' }}>
                        12.&nbsp;&nbsp;&nbsp;Screenshot cropped hai — full screen + notification bar dikhna chahiye.
                    </Typography>
                </Stack>

                {/* Correct Screenshot Section */}
                <Box sx={{ mb: 6, mt: 4 }}>
                    <Typography sx={{ fontWeight: 700, mb: 3, color: '#000', textAlign: 'center', fontFamily: 'SF Pro Display', fontSize: '32px' }}>
                        Correct Screenshot
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        <img
                            src="/correct_img_new.png"
                            alt="Correct Screenshot Format"
                            style={{
                                maxWidth: '100%',
                                height: 'auto',
                                borderRadius: '8px',
                                display: 'block'
                            }}
                        />
                    </Box>
                </Box>

                {/* Resubmission Form */}
                <Typography
                    id="upload-form-section"
                    variant="h5"
                    sx={{
                        fontWeight: 700,
                        fontFamily: 'SF Pro Display',
                        color: '#3F51B5',
                        position: 'relative',
                        display: 'block',
                        textAlign: 'center',
                        mb: 3
                    }}
                >
                    Upload Your{' '}
                    <Box component="span" sx={{ position: 'relative', display: 'inline-block', zIndex: 1 }}>
                        Screenshot
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: '4px',
                                left: '-2px',
                                right: '-8px',
                                height: '14px',
                                backgroundColor: '#FFD54F',
                                zIndex: -1,
                                opacity: 1,
                                borderRadius: '10px 4px 8px 2px',
                                transform: 'rotate(-2deg)',
                                boxShadow: '0 0 4px #FFD54F'
                            }}
                        />
                    </Box>
                </Typography>

                {errorMessage && (
                    <Alert
                        severity="error"
                        icon={<ErrorOutlineIcon />}
                        onClose={() => setErrorMessage('')}
                        sx={{
                            mb: 3,
                            borderRadius: '12px',
                            '& .MuiAlert-message': {
                                width: '100%',
                                whiteSpace: 'pre-line'
                            }
                        }}
                    >
                        {errorMessage}
                    </Alert>
                )}

                <Stack spacing={3} sx={{ mx: 1 }}>
                    <Box>
                        <Typography variant="body1" sx={{ mb: 1, fontWeight: 600, color: '#000', fontFamily: 'SF Pro Display' }}>
                            Enter Your Phone No:
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="Enter your phone number"
                            value={phone}
                            onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '');
                                if (value.length === 0) {
                                    setPhone('');
                                    if (errorMessage) setErrorMessage('');
                                    return;
                                }
                                if (!['6', '7', '8', '9'].includes(value[0])) {
                                    return;
                                }
                                if (value.length <= 10) {
                                    setPhone(value);
                                }
                                if (errorMessage) setErrorMessage('');
                            }}
                            type="tel"
                            error={!!errorMessage && errorMessage.toLowerCase().includes('phone')}
                            disabled={isSubmitting}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '12px',
                                    bgcolor: '#fff',
                                    fontFamily: 'SF Pro Display',
                                    '& fieldset': {
                                        borderColor: '#D0D0D0',
                                        borderWidth: '1.5px',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#3F51B5',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#3F51B5',
                                    },
                                },
                            }}
                        />
                    </Box>

                    <Box>
                        <Typography variant="body1" sx={{ mb: 1, fontWeight: 600, color: '#000', fontFamily: 'SF Pro Display' }}>
                            Enter Your UPI ID
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="Enter your UPI ID"
                            value={upi}
                            onChange={(e) => {
                                setUpi(e.target.value);
                                if (errorMessage) setErrorMessage('');
                            }}
                            error={!!errorMessage && errorMessage.toLowerCase().includes('upi')}
                            disabled={isSubmitting}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '12px',
                                    bgcolor: '#fff',
                                    fontFamily: 'SF Pro Display',
                                    '& fieldset': {
                                        borderColor: '#D0D0D0',
                                        borderWidth: '1.5px',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#3F51B5',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#3F51B5',
                                    },
                                },
                            }}
                        />
                    </Box>

                    <Box>
                        <Typography variant="body1" sx={{ mb: 1, fontWeight: 600, color: '#000', fontFamily: 'SF Pro Display' }}>
                            Upload Your Screenshot
                        </Typography>
                        <Box
                            sx={{
                                border: `1.5px solid ${errorMessage && errorMessage.toLowerCase().includes('screenshot') ? '#d32f2f' : '#D0D0D0'}`,
                                borderRadius: '12px',
                                p: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                bgcolor: '#fff',
                            }}
                        >
                            <Button
                                component="label"
                                variant="contained"
                                disabled={isSubmitting}
                                sx={{
                                    bgcolor: '#E0E0E0',
                                    color: '#666',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    boxShadow: 'none',
                                    '&:hover': {
                                        bgcolor: '#D0D0D0',
                                        boxShadow: 'none',
                                    },
                                    borderRadius: '8px',
                                    px: 3,
                                }}
                            >
                                Choose file
                                <input
                                    type="file"
                                    hidden
                                    accept="image/*"
                                    onChange={(e) => {
                                        if (e.target.files && e.target.files[0]) {
                                            if (e.target.files[0].size > 5 * 1024 * 1024) {
                                                setErrorMessage('File size must be less than 5MB');
                                                return;
                                            }
                                            setFile(e.target.files[0]);
                                            if (errorMessage) setErrorMessage('');
                                        }
                                    }}
                                    disabled={isSubmitting}
                                />
                            </Button>
                            <Typography variant="body2" sx={{ color: '#999', fontFamily: 'SF Pro Display' }}>
                                {file ? file.name : 'No file chosen'}
                            </Typography>
                        </Box>
                    </Box>

                    <Button
                        variant="contained"
                        fullWidth
                        onClick={handleSubmit}
                        disabled={isSubmitting || !phone || !upi || !file}
                        sx={{
                            bgcolor: '#FFC107',
                            color: '#000',
                            fontWeight: 700,
                            py: 2,
                            borderRadius: '12px',
                            fontSize: '1.1rem',
                            textTransform: 'none',
                            boxShadow: 'none',
                            fontFamily: 'SF Pro Display',
                            '&:hover': {
                                bgcolor: '#FFB300',
                                boxShadow: 'none',
                            },
                            '&:disabled': {
                                bgcolor: '#E0E0E0',
                                color: '#999'
                            }
                        }}
                    >
                        {isSubmitting ? (
                            <>
                                <CircularProgress size={20} sx={{ mr: 1, color: '#666' }} />
                                Submitting...
                            </>
                        ) : (
                            'Submit'
                        )}
                    </Button>
                </Stack>
            </Container>
        </Box>
    );
}
